import { useId, useLayoutEffect, useRef, useState } from "react";

import type { CardOut, SessionResultOut } from "../contract/api";
import { t } from "../contract/strings";
import { cx, speakerName } from "../ui";
import { AnonymousSpeakerName } from "./anonymous-speaker-name";
import { MomentLink } from "./result-actions";
import { LABEL, cardQuote, fmtMinSec } from "./result-model";

/**
 * A speaker's quote, clamped to two lines with an ellipsis (#772). When it is longer, a
 * tap on the text opens it fully and another closes it: a transparent button over the
 * text, named "Show the whole quote", with `aria-expanded`. The text itself stays a
 * paragraph, so a screen reader reads all of it either way; the clamp is visual only. A
 * quote that fits in two lines gets no button, measured rather than guessed from its
 * length, and measured again when the card's width changes.
 */
function QuoteText({ text }: { text: string }) {
  const id = useId();
  const ref = useRef<HTMLParagraphElement>(null);
  const [open, setOpen] = useState(false);
  const [overflows, setOverflows] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    // Measured only while clamped: open, the text never overflows.
    if (!el || open) return;
    const measure = () => setOverflows(el.scrollHeight > el.clientHeight + 1);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [text, open]);

  return (
    <div className="relative">
      <p
        id={id}
        ref={ref}
        data-quote-text
        className={cx("m-0 text-note leading-normal text-ink italic", !open && "line-clamp-2")}
      >
        “{text}”
      </p>
      {overflows ? (
        <button
          type="button"
          data-quote-toggle
          aria-expanded={open}
          aria-controls={id}
          aria-label={t("result.quote_expand")}
          onClick={() => setOpen((value) => !value)}
          className="absolute inset-0 p-0 bg-transparent border-none rounded-control cursor-pointer"
        />
      ) : null}
    </div>
  );
}

/**
 * One colour scheme for every speaker's card (#766): the accent avatar and the accent chip.
 * The transcript keeps its per-speaker colours; the cards do not rotate them.
 */
const AVATAR = "bg-accent";
const TOPIC_CHIP = "bg-accent/10 text-accent border-accent/20";

/**
 * The letter in a person's avatar: an anonymous voice's own letter ("Speaker A" → "A"),
 * else the first letter of their name.
 */
export function avatarInitial(name: string): string {
  const anonymous = /^Speaker\s+(\S+)$/i.exec(name.trim());
  const source = anonymous ? anonymous[1] : name.trim();
  return (Array.from(source)[0] ?? "?").toLocaleUpperCase();
}

/**
 * One person, as a light mini-card inside the Speakers section (#766): an initial avatar
 * and their name (the creator renames an anonymous voice, #761) on the left, the minutes
 * they spoke on the right; their topic chips; their follow-up draft on a tinted inset, or
 * the no-summary note in its place. Every card has the same colours. No share bar, no
 * Copy, no count.
 */
export function PersonCard({
  result,
  card,
  onJump,
  onSpeakerRenamed,
}: {
  result: SessionResultOut;
  card: CardOut;
  onJump: (time: number) => void;
  onSpeakerRenamed: (next: SessionResultOut) => void;
}) {
  const topics = card.topics ?? [];
  const name = speakerName(card.speaker);
  const quote = cardQuote(card);
  return (
    <li
      data-person-card
      className="p-3 bg-surface border border-line rounded-card flex flex-col gap-2"
    >
      <div className="flex justify-between items-center gap-4">
        <div className="flex items-center gap-2 min-w-0">
          <span
            aria-hidden="true"
            data-avatar
            className={cx(
              "flex-none inline-flex items-center justify-center size-icon-lg rounded-full font-code font-bold text-count text-accent-ink",
              AVATAR,
            )}
          >
            {avatarInitial(name)}
          </span>
          <div className="flex flex-wrap items-center min-w-0">
            <AnonymousSpeakerName
              sessionId={result.session_id}
              name={name}
              canRename={result.can_rename === true && card.speaker_kind === "anonymous"}
              // The reference's name: the display face, semibold, 14, normal case.
              className="font-title font-semibold text-sm leading-tight text-ink min-w-0 truncate"
              iconClassName="size-icon-sm"
              onRenamed={onSpeakerRenamed}
            />
          </div>
        </div>
        <span className="flex-none font-code font-bold text-count text-accent tabular-nums">
          {fmtMinSec(card.spoke_sec)}
        </span>
      </div>

      {topics.length ? (
        // The generated text's language (#772); the quote below keeps its own speech.
        <div data-topics lang={result.language ?? undefined} className="flex flex-wrap gap-1">
          {topics.map((topic) => (
            <span
              key={topic}
              className={cx(
                "font-code text-count font-semibold tracking-label border rounded-pill py-hair px-chip-x",
                TOPIC_CHIP,
              )}
            >
              {topic}
            </span>
          ))}
        </div>
      ) : null}

      {/*
        Say the summary is missing rather than showing an empty card that reads as a quiet
        participant (#274). Only on an explicit "none": an older result carries no status
        and must not be labelled either way.
      */}
      {card.summary_status === "none" ? (
        <p className="m-0 text-note leading-tight text-ink-soft italic">
          {t("result.no_summary_participant")}
        </p>
      ) : quote ? (
        <>
          {/*
            The key quote (#772) takes the tinted inset: their own words, checked by the
            server against their own lines, with a jump to the moment they said them.
          */}
          <figure
            data-quote
            aria-label={t("result.quote_aria", { name })}
            className="relative m-0 p-2 pr-8 bg-surface-2 border border-line rounded-control"
          >
            <blockquote className="m-0">
              <QuoteText text={quote.text} />
            </blockquote>
            <MomentLink data-quote-jump at={quote.source_start} onJump={onJump} />
          </figure>
          {card.follow_up_draft ? (
            <p
              data-follow-up
              lang={result.language ?? undefined}
              className="m-0 text-note leading-normal text-ink-soft"
            >
              {card.follow_up_draft}
            </p>
          ) : null}
        </>
      ) : card.follow_up_draft ? (
        <p
          data-follow-up
          lang={result.language ?? undefined}
          className="m-0 p-2 bg-surface-2 border border-line rounded-control text-note leading-normal text-ink-soft"
        >
          {card.follow_up_draft}
        </p>
      ) : null}
    </li>
  );
}

/**
 * Quick's Speakers section (#766): one tinted card holding a light mini-card per person,
 * headed "Speakers" with how many there are (the number on screen, "4 speakers" for a
 * screen reader).
 */
export function PeopleSection({
  result,
  onJump,
  onSpeakerRenamed,
}: {
  result: SessionResultOut;
  onJump: (time: number) => void;
  onSpeakerRenamed: (next: SessionResultOut) => void;
}) {
  // A result written before the field existed omits `cards`.
  const cards = result.cards ?? [];
  if (!cards.length) return null;
  return (
    <section
      data-people
      aria-labelledby="result-speakers-heading"
      className="p-4 bg-surface-2 border border-line rounded-card flex flex-col gap-stack"
    >
      <div className="flex items-center justify-between gap-2">
        {/* A heading that names the section, drawn exactly as the label it replaced. */}
        <h3 id="result-speakers-heading" className={cx(LABEL, "m-0 text-label text-ink-soft")}>
          {t("result.speakers_heading")}
        </h3>
        {/* "4 Participants": small muted mono, in its own case with normal tracking. */}
        <span data-speakers-count className="font-code font-semibold text-count text-ink-soft tabular-nums">
          {t("result.speakers_count", { count: cards.length })}
        </span>
      </div>
      <ul className="m-0 p-0 list-none flex flex-col gap-2">
        {cards.map((card) => (
          <PersonCard
            key={card.participant_id ?? `anonymous:${card.speaker}`}
            result={result}
            card={card}
            onJump={onJump}
            onSpeakerRenamed={onSpeakerRenamed}
          />
        ))}
      </ul>
    </section>
  );
}
